// Trends section with tabs: Combined / Weight / Calories.
// Custom zero-dep SVG charts:
//   Combined tab: weight line+area on left axis + calorie bars on right axis, aligned by date.
//   Weight tab: polyline + area fill with dashed target reference line.
//   Calorie tab: vertical bar chart.

import { useState } from 'react'
import type { DashboardWeightPoint, DashboardCaloriePoint, DashboardTargets } from '../lib/types'
import type { Strings } from '../lib/i18n'
import { kgToLb } from '../lib/units'

type Tab = 'combined' | 'weight' | 'calories'

interface Props {
  weightSeries: DashboardWeightPoint[]
  calorieSeries: DashboardCaloriePoint[]
  targets: DashboardTargets
  targetWeightKg: number | null
  strings: Strings
}

// Compact date label: YYYY-MM-DD → MM-DD.
function shortDate(d: string) {
  return d.slice(5)
}

const CHART_W = 300
const CHART_H = 120
const PAD_L = 36
const PAD_R = 36   // wider right pad for dual-axis right label
const PAD_T = 10
const PAD_B = 24

function xScale(i: number, n: number) {
  if (n <= 1) return PAD_L
  return PAD_L + (i / (n - 1)) * (CHART_W - PAD_L - PAD_R)
}

function yScale(v: number, min: number, max: number) {
  const span = Math.max(max - min, 0.01)
  return PAD_T + ((max - v) / span) * (CHART_H - PAD_T - PAD_B)
}

function WeightLine({
  series,
  targetKg,
  noDataText,
  targetLabel,
  ariaLabel,
}: {
  series: DashboardWeightPoint[]
  targetKg: number | null
  noDataText: string
  targetLabel: string
  ariaLabel: string
}) {
  if (series.length === 0) {
    return (
      <svg viewBox={`0 0 ${CHART_W} ${CHART_H}`} className="trend-svg">
        <text x={CHART_W / 2} y={CHART_H / 2} textAnchor="middle" fill="var(--fg-subtle)" fontSize="12">
          {noDataText}
        </text>
      </svg>
    )
  }

  const kgs = series.map((p) => p.kg)
  const rawMin = targetKg == null ? Math.min(...kgs) : Math.min(...kgs, targetKg)
  const rawMax = targetKg == null ? Math.max(...kgs) : Math.max(...kgs, targetKg)
  const span = rawMax - rawMin || 1
  const min = rawMin - span * 0.1
  const max = rawMax + span * 0.1
  const n = series.length

  const pts = series.map((p, i) => ({ x: xScale(i, n), y: yScale(p.kg, min, max) }))
  const polyline = pts.map((p) => `${p.x},${p.y}`).join(' ')
  const areaPath =
    `M ${pts[0].x},${PAD_T + CHART_H - PAD_B} ` +
    pts.map((p) => `L ${p.x},${p.y}`).join(' ') +
    ` L ${pts[pts.length - 1].x},${PAD_T + CHART_H - PAD_B} Z`
  const targetY = targetKg == null ? null : yScale(targetKg, min, max)

  const yLabels = [
    { v: rawMax, y: yScale(rawMax, min, max) },
    { v: rawMin, y: yScale(rawMin, min, max) },
  ]
  const xLabels =
    n >= 2
      ? [
          { label: shortDate(series[0].date), x: xScale(0, n) },
          { label: shortDate(series[n - 1].date), x: xScale(n - 1, n) },
        ]
      : []

  return (
    <svg viewBox={`0 0 ${CHART_W} ${CHART_H}`} className="trend-svg" role="img" aria-label={ariaLabel}>
      <defs>
        <linearGradient id="wt-area-grad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="var(--brand-primary)" stopOpacity="0.25" />
          <stop offset="100%" stopColor="var(--brand-primary)" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={areaPath} fill="url(#wt-area-grad)" />
      {targetKg != null && targetY != null && (
        <>
          <line
            x1={PAD_L}
            y1={targetY}
            x2={CHART_W - PAD_R}
            y2={targetY}
            stroke="var(--brand-primary)"
            strokeWidth="1"
            strokeDasharray="4 3"
            opacity="0.5"
          />
          <text x={CHART_W - PAD_R - 2} y={targetY - 3} fontSize="8" fill="var(--brand-primary)" textAnchor="end">
            {targetLabel} {kgToLb(targetKg).toFixed(1)}
          </text>
        </>
      )}
      <polyline
        points={polyline}
        fill="none"
        stroke="var(--brand-primary)"
        strokeWidth="2"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      {pts.length > 0 && (
        <>
          <circle cx={pts[0].x} cy={pts[0].y} r="3" fill="var(--brand-primary)" opacity="0.5" />
          <circle cx={pts[pts.length - 1].x} cy={pts[pts.length - 1].y} r="4" fill="var(--brand-primary)" />
        </>
      )}
      {yLabels.map((l) => (
        <text key={l.v} x={PAD_L - 4} y={l.y + 4} fontSize="9" fill="var(--fg-muted)" textAnchor="end">
          {kgToLb(l.v).toFixed(1)}
        </text>
      ))}
      {xLabels.map((l) => (
        <text key={l.x} x={l.x} y={CHART_H - 4} fontSize="9" fill="var(--fg-muted)" textAnchor="middle">
          {l.label}
        </text>
      ))}
    </svg>
  )
}

function CalorieBars({
  series,
  target,
  noDataText,
  targetLabel,
  ariaLabel,
}: {
  series: DashboardCaloriePoint[]
  target: number
  noDataText: string
  targetLabel: string
  ariaLabel: string
}) {
  if (series.length === 0) {
    return (
      <svg viewBox={`0 0 ${CHART_W} ${CHART_H}`} className="trend-svg">
        <text x={CHART_W / 2} y={CHART_H / 2} textAnchor="middle" fill="var(--fg-subtle)" fontSize="12">
          {noDataText}
        </text>
      </svg>
    )
  }

  const cals = series.map((p) => p.calories)
  const maxCal = Math.max(...cals, target, 1)
  const n = series.length
  const barW = Math.max(3, ((CHART_W - PAD_L - PAD_R) / n) - 2)
  const bottomY = CHART_H - PAD_B
  const targetY = PAD_T + ((maxCal - target) / maxCal) * (bottomY - PAD_T)

  return (
    <svg viewBox={`0 0 ${CHART_W} ${CHART_H}`} className="trend-svg" role="img" aria-label={ariaLabel}>
      {series.map((p, i) => {
        const x = xScale(i, n) - barW / 2
        const barH = (p.calories / maxCal) * (bottomY - PAD_T)
        const y = bottomY - barH
        const over = p.calories > target
        return (
          <rect
            key={p.date}
            x={x}
            y={y}
            width={barW}
            height={Math.max(barH, 0)}
            fill={over ? 'var(--error)' : 'var(--brand-primary)'}
            opacity="0.75"
            rx="2"
          />
        )
      })}
      <line
        x1={PAD_L}
        y1={targetY}
        x2={CHART_W - PAD_R}
        y2={targetY}
        stroke="var(--brand-primary)"
        strokeWidth="1"
        strokeDasharray="4 3"
        opacity="0.6"
      />
      <text x={CHART_W - PAD_R - 2} y={targetY - 3} fontSize="8" fill="var(--brand-primary)" textAnchor="end">
        {targetLabel} {target}
      </text>
      {n >= 2 && (
        <>
          <text x={xScale(0, n)} y={CHART_H - 4} fontSize="9" fill="var(--fg-muted)" textAnchor="middle">
            {shortDate(series[0].date)}
          </text>
          <text x={xScale(n - 1, n)} y={CHART_H - 4} fontSize="9" fill="var(--fg-muted)" textAnchor="middle">
            {shortDate(series[n - 1].date)}
          </text>
        </>
      )}
    </svg>
  )
}

// ── Combined chart: weight line (left axis) + calorie bars (right axis) ──────
// Dates are aligned by merging weight_series and calorie_series on date.
// Left axis: lb (converted from stored kg). Right axis: kcal. Bars render first (behind), then weight line.

function CombinedChart({
  weightSeries,
  calorieSeries,
  targetKg,
  calTarget,
  noDataText,
  targetLabel,
  ariaLabel,
}: {
  weightSeries: DashboardWeightPoint[]
  calorieSeries: DashboardCaloriePoint[]
  targetKg: number | null
  calTarget: number
  noDataText: string
  targetLabel: string
  ariaLabel: string
}) {
  if (weightSeries.length === 0 && calorieSeries.length === 0) {
    return (
      <svg viewBox={`0 0 ${CHART_W} ${CHART_H}`} className="trend-svg">
        <text x={CHART_W / 2} y={CHART_H / 2} textAnchor="middle" fill="var(--fg-subtle)" fontSize="12">
          {noDataText}
        </text>
      </svg>
    )
  }

  // Build a unified date index across both series.
  const allDates = Array.from(
    new Set([...weightSeries.map((p) => p.date), ...calorieSeries.map((p) => p.date)])
  ).sort()
  const n = allDates.length
  if (n === 0) {
    return null
  }

  const weightByDate = Object.fromEntries(weightSeries.map((p) => [p.date, p.kg]))
  const calByDate = Object.fromEntries(calorieSeries.map((p) => [p.date, p.calories]))

  // Weight scale (left axis).
  const kgs = weightSeries.map((p) => p.kg)
  const hasWeight = kgs.length > 0
  const wRawMin = hasWeight ? (targetKg == null ? Math.min(...kgs) : Math.min(...kgs, targetKg)) : (targetKg ?? 0)
  const wRawMax = hasWeight ? (targetKg == null ? Math.max(...kgs) : Math.max(...kgs, targetKg)) : (targetKg ?? 0)
  const wSpan = wRawMax - wRawMin || 1
  const wMin = wRawMin - wSpan * 0.1
  const wMax = wRawMax + wSpan * 0.1

  // Calorie scale (right axis).
  const cals = calorieSeries.map((p) => p.calories)
  const hasCal = cals.length > 0
  const maxCal = hasCal ? Math.max(...cals, calTarget, 1) : Math.max(calTarget, 1)
  const bottomY = CHART_H - PAD_B

  // Bar width — shared across all dates.
  const barW = Math.max(3, ((CHART_W - PAD_L - PAD_R) / n) - 2)

  // Weight line points (only dates where weight exists).
  const wPts = allDates
    .map((date, i) => {
      const kg = weightByDate[date]
      if (kg === undefined) return null
      return { x: xScale(i, n), y: yScale(kg, wMin, wMax) }
    })
    .filter((p): p is { x: number; y: number } => p !== null)

  const polyline = wPts.map((p) => `${p.x},${p.y}`).join(' ')
  const areaPath =
    wPts.length > 0
      ? `M ${wPts[0].x},${bottomY} ` +
        wPts.map((p) => `L ${p.x},${p.y}`).join(' ') +
        ` L ${wPts[wPts.length - 1].x},${bottomY} Z`
      : ''

  const wTargetY = hasWeight && targetKg != null ? yScale(targetKg, wMin, wMax) : null

  // Left-axis labels.
  const wYLabels = hasWeight
    ? [
        { v: wRawMax, y: yScale(wRawMax, wMin, wMax) },
        { v: wRawMin, y: yScale(wRawMin, wMin, wMax) },
      ]
    : []

  // Right-axis calorie label (max).
  const calMaxLabel = hasCal ? Math.round(maxCal).toString() : ''

  return (
    <svg viewBox={`0 0 ${CHART_W} ${CHART_H}`} className="trend-svg" role="img" aria-label={ariaLabel}>
      <defs>
        <linearGradient id="combined-area-grad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="var(--brand-primary)" stopOpacity="0.2" />
          <stop offset="100%" stopColor="var(--brand-primary)" stopOpacity="0" />
        </linearGradient>
      </defs>

      {/* Calorie bars (background layer) */}
      {allDates.map((date, i) => {
        const cal = calByDate[date]
        if (cal === undefined) return null
        const barH = (cal / maxCal) * (bottomY - PAD_T)
        const x = xScale(i, n) - barW / 2
        const y = bottomY - barH
        const over = calTarget > 0 && cal > calTarget
        return (
          <rect
            key={date}
            x={x}
            y={y}
            width={barW}
            height={Math.max(barH, 0)}
            fill={over ? 'var(--error)' : 'var(--brand-secondary, #e5c7d0)'}
            opacity="0.55"
            rx="2"
          />
        )
      })}

      {/* Calorie target line */}
      {calTarget > 0 && (
        <>
          {(() => {
            const calTargetY = PAD_T + ((maxCal - calTarget) / maxCal) * (bottomY - PAD_T)
            return (
              <line
                x1={PAD_L}
                y1={calTargetY}
                x2={CHART_W - PAD_R}
                y2={calTargetY}
                stroke="var(--brand-primary)"
                strokeWidth="1"
                strokeDasharray="2 3"
                opacity="0.3"
              />
            )
          })()}
        </>
      )}

      {/* Weight area fill */}
      {areaPath && <path d={areaPath} fill="url(#combined-area-grad)" />}

      {/* Weight target dashed line */}
      {wTargetY !== null && (
        <line
          x1={PAD_L}
          y1={wTargetY}
          x2={CHART_W - PAD_R}
          y2={wTargetY}
          stroke="var(--brand-primary)"
          strokeWidth="1"
          strokeDasharray="4 3"
          opacity="0.5"
        />
      )}

      {/* Weight polyline */}
      {wPts.length > 1 && (
        <polyline
          points={polyline}
          fill="none"
          stroke="var(--brand-primary)"
          strokeWidth="2"
          strokeLinejoin="round"
          strokeLinecap="round"
        />
      )}
      {wPts.length > 0 && (
        <>
          <circle cx={wPts[0].x} cy={wPts[0].y} r="3" fill="var(--brand-primary)" opacity="0.5" />
          <circle cx={wPts[wPts.length - 1].x} cy={wPts[wPts.length - 1].y} r="4" fill="var(--brand-primary)" />
        </>
      )}

      {/* Left axis: weight labels */}
      {wYLabels.map((l) => (
        <text key={l.v} x={PAD_L - 4} y={l.y + 4} fontSize="9" fill="var(--fg-muted)" textAnchor="end">
          {kgToLb(l.v).toFixed(1)}
        </text>
      ))}

      {/* Right axis: calorie max label */}
      {calMaxLabel && (
        <text x={CHART_W - 2} y={PAD_T + 4} fontSize="9" fill="var(--fg-muted)" textAnchor="end" opacity="0.7">
          {calMaxLabel}
        </text>
      )}

      {/* X-axis: first and last date */}
      {n >= 2 && (
        <>
          <text x={xScale(0, n)} y={CHART_H - 4} fontSize="9" fill="var(--fg-muted)" textAnchor="middle">
            {shortDate(allDates[0])}
          </text>
          <text x={xScale(n - 1, n)} y={CHART_H - 4} fontSize="9" fill="var(--fg-muted)" textAnchor="middle">
            {shortDate(allDates[n - 1])}
          </text>
        </>
      )}

      {/* Weight target label at right edge */}
      {wTargetY !== null && hasWeight && targetKg != null && (
        <text x={CHART_W - PAD_R - 2} y={wTargetY - 3} fontSize="8" fill="var(--brand-primary)" textAnchor="end">
          {targetLabel} {kgToLb(targetKg).toFixed(1)}
        </text>
      )}
    </svg>
  )
}

export function TrendChart({ weightSeries, calorieSeries, targets, targetWeightKg, strings: s }: Props) {
  const [tab, setTab] = useState<Tab>('combined')

  const tabs: Array<{ key: Tab; label: string }> = [
    { key: 'combined', label: s.tabCombined },
    { key: 'weight', label: s.tabWeight },
    { key: 'calories', label: s.tabCalories },
  ]

  return (
    <div className="dash-card dash-trend-card">
      <div className="dash-card-title">{s.trendTitle}</div>
      <div className="dash-tabs">
        {tabs.map((t) => (
          <button
            key={t.key}
            type="button"
            className={`dash-tab${tab === t.key ? ' dash-tab-active' : ''}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'combined' && (
        <CombinedChart
          weightSeries={weightSeries}
          calorieSeries={calorieSeries}
          targetKg={targetWeightKg}
          calTarget={targets.daily_calories}
          noDataText={s.trendNoData}
          targetLabel={s.trendTargetLabel}
          ariaLabel={s.tabCombined}
        />
      )}
      {tab === 'weight' && (
        <WeightLine
          series={weightSeries}
          targetKg={targetWeightKg}
          noDataText={s.trendNoData}
          targetLabel={s.trendTargetLabel}
          ariaLabel={s.tabWeight}
        />
      )}
      {tab === 'calories' && (
        <CalorieBars
          series={calorieSeries}
          target={targets.daily_calories}
          noDataText={s.trendNoData}
          targetLabel={s.trendTargetLabel}
          ariaLabel={s.tabCalories}
        />
      )}
    </div>
  )
}
